import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import URLEncoder from "./pages/URLEncoder";
import URLDecoder from "./pages/URLDecoder";
import URLScraper from "./pages/URLScraper";
import "./index.css";
import RemoveLineBreaks from "./pages/RemoveLineBreaks";
import RenderString from "./pages/RenderString";
import RenderMarkdown from "./pages/RenderMarkdown";
import QueryParamEditor from "./pages/QueryParamEditor";
import JSONFormatter from "./pages/JSONFormatter";
import ApiTester from "./pages/APITester";
import SMTPTester from "./pages/SMTPTester";
import TimezoneConverter from "./pages/TimeZoneConverter";
import PdfToText from "./pages/pdfToText";
import PdfToHTML from "./pages/PdfToHTML";
import PdfSplitter from "./pages/PdfSplitter";
import PdfCompressor from "./pages/PdfCompressor";
import PdfToImage from "./pages/pdfToImage";
import ImageResizer from "./pages/ImageResizer";
import TextToSpeech from "./pages/TextToSpeech";
import TextCompare from "./pages/TextCompare";
import TextCounter from "./pages/TextCounter";
import JSPlayground from "./pages/JavascriptPlayground";
import EmailVerifier from "./pages/EmailVerifier";
import TokenCounter from "./pages/TokenCounter";
import MultiModelPlayGround from "./pages/MultiModelPlayGround";
import MultiModelImageGenerator from "./pages/MultiModelImageGenerator";
import MemeCreator from "./pages/MemeCreator";
import MultiModelTranscriptionTester from "./pages/MultiModelTranscriptionTester";
import ScreenRecorder from './pages/ScreenRecorder';
import PdfMerger from "./pages/PdfMerger";
import YoutubeToMP3 from "./pages/YoutubeToMP3";
import AudioQuiz from "./pages/AudioQuiz";
import AudioQuizTake from "./pages/AudioQuizTake";
import UrlShortener from "./pages/UrlShortener";
import SharedRecording from "./pages/SharedRecording";
import CodeReviewer from "./pages/CodeReviewer";
import FileToLink from "./pages/FileToLink";
import ClipboardToLink from "./pages/ClipboardToLink";
import Help from "./pages/Help";
import Login from "./pages/Login";
import BlogList from "./blogs/BlogList";
import LeadAudience from "./leads/Audience";
import BlogDetail from "./blogs/BlogDetails";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import LinkedInCallback from './components/LinkedInCallback'; 
import HtmlPlayground from "./pages/HtmlPlayground";
import HtmlPlaygroundPreview from "./pages/HtmlPlaygroundPreview";
import QRCodeGenerator from './pages/QRCodeGenerator';
import PdfToCsv from "./pages/pdfToCsv";
import ErrorLogging from "./pages/ErrorLogging";
import BackgroundRemover from "./pages/BackgroundRemover";
import OcrImage from "./pages/OcrImage";
import JWTDecoder from "./pages/JWTDecoder";
import UUIDGenerator from "./pages/UUIDGenerator";
import ColorConverter from "./pages/ColorConverter";
import ImagesToPdf from "./pages/ImagesToPdf";
import PasswordGenerator from "./pages/PasswordGenerator";
import CaseConverter from "./pages/CaseConverter";
import RegexTester from "./pages/RegexTester";
import CronTranslator from "./pages/CronTranslator";
import TimestampConverter from "./pages/TimestampConverter";
import Base64Codec from "./pages/Base64Codec";
import HttpStatusLookup from "./pages/HttpStatusLookup";
import Slugifier from "./pages/Slugifier";
import HtmlEntityCodec from "./pages/HtmlEntityCodec";
import ChmodCalculator from "./pages/ChmodCalculator";
import CidrCalculator from "./pages/CidrCalculator";
import CsvJsonConverter from "./pages/CsvJsonConverter";
import LoremIpsumGenerator from "./pages/LoremIpsumGenerator";
import TotpGenerator from "./pages/TotpGenerator";
import UlidGenerator from "./pages/UlidGenerator";
import HashGenerator from "./pages/HashGenerator";
import NumberBaseConverter from "./pages/NumberBaseConverter";
import JsonYamlConverter from "./pages/JsonYamlConverter";
import CssUnitConverter from "./pages/CssUnitConverter";
import HmacGenerator from "./pages/HmacGenerator";
import MarkdownTableGenerator from "./pages/MarkdownTableGenerator";
import JsonToTypescript from "./pages/JsonToTypescript";
import MimeTypeLookup from "./pages/MimeTypeLookup";
import ByteSizeConverter from "./pages/ByteSizeConverter";
import ColorContrastChecker from "./pages/ColorContrastChecker";
import XmlFormatter from "./pages/XmlFormatter";
import DateDifferenceCalculator from "./pages/DateDifferenceCalculator";
import MarkdownTocGenerator from "./pages/MarkdownTocGenerator";
import UnicodeInspector from "./pages/UnicodeInspector";
import BoxShadowGenerator from "./pages/BoxShadowGenerator";
import CssGradientGenerator from "./pages/CssGradientGenerator";
import CssClampGenerator from "./pages/CssClampGenerator";
import JsonPathTester from "./pages/JsonPathTester";
import LineSorter from "./pages/LineSorter";
import MongoObjectIdInspector from "./pages/MongoObjectIdInspector";
import SemverComparator from "./pages/SemverComparator";
import CssSpecificityCalculator from "./pages/CssSpecificityCalculator";
import KeywordResearch from "./pages/KeywordResearch";
import RomanNumeralConverter from "./pages/RomanNumeralConverter";
import UrlParser from "./pages/UrlParser";
import Base32Codec from "./pages/Base32Codec";
import PunycodeConverter from "./pages/PunycodeConverter";
import LuhnValidator from "./pages/LuhnValidator";
import RequestInspector from "./pages/RequestInspector";


import { Toaster } from "react-hot-toast";
import "./components/i18n";
import { withTrailingSlash } from "./utils/paths";

// Language Enforcer Component
function LanguageEnforcer({ children }) {
  const location = useLocation();
  
  // Skip language enforcement for specific paths
  const skipPaths = ['/r/', '/auth/linkedin/callback'];
  
  const shouldSkip = skipPaths.some(path =>
    location.pathname.startsWith(path)
  );

  if (shouldSkip) {
    return children;
  }
  
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const supportedLangs = ["en", "es", "zh-CN", "de", "fr", "ja"];
  const hasLang = pathSegments.length > 0 && supportedLangs.includes(pathSegments[0]);

  if (!hasLang) {
    return <Navigate to={`${withTrailingSlash(`/en${location.pathname}`)}${location.search}`} replace />;
  }

  return children;
}

// Fallback with Query component
function FallbackWithQuery({ to }) {
  const location = useLocation();
  return <Navigate to={`${withTrailingSlash(to)}${location.search}`} replace />;
}

function App() {
  return (
    <Router>
      <Toaster position="top-right" reverseOrder={false} />
      <LanguageEnforcer>
        <Routes>
          {/* Put critical routes at the top */}
          <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
          <Route path="/r/:slug" element={<SharedRecording />} />

          <Route path="" element={<Navigate to="/en/" replace />} />
          <Route path="/:lang" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="tools/url-encoder" element={<URLEncoder />} />
            <Route path="tools/url-decoder" element={<URLDecoder />} />
            <Route path="tools/url-scraper" element={<URLScraper />} />
            <Route path="tools/remove-line-breaks" element={<RemoveLineBreaks />} />
            <Route path="tools/render-string" element={<RenderString />} />
            <Route path="tools/render-markdown" element={<RenderMarkdown />} />
            <Route path="tools/query-param-editor" element={<QueryParamEditor />} />
            <Route path="tools/json-formatter" element={<JSONFormatter />} />
            <Route path="tools/api-tester" element={<ApiTester />} />
            <Route path="tools/smtp-tester" element={<SMTPTester />} />
            <Route path="tools/timezone-converter" element={<TimezoneConverter />} />
            <Route path="tools/image-resizer" element={<ImageResizer />} />
            <Route path="tools/pdf-to-text-converter" element={<PdfToText />} />
            <Route path="tools/pdf-to-csv-converter" element={<PdfToCsv />} />
            <Route path="tools/pdf-to-html-converter" element={<PdfToHTML />} />
            <Route path="tools/pdf-splitter" element={<PdfSplitter />} />
            <Route path="tools/pdf-to-image-converter" element={<PdfToImage />} />
            <Route path="tools/pdf-compressor" element={<PdfCompressor />} />
            <Route path="tools/text-to-speech" element={<TextToSpeech />} />
            <Route path="tools/text-compare" element={<TextCompare />} />
            <Route path="tools/text-counter" element={<TextCounter />} />
            <Route path="tools/js-playground" element={<JSPlayground />} />
            <Route path="tools/email-verifier" element={<EmailVerifier />} />
            <Route path="tools/token-counter" element={<TokenCounter />} />
            <Route path="tools/multi-model-playground" element={<MultiModelPlayGround />} />
            <Route path="tools/multi-model-transcription" element={<MultiModelTranscriptionTester />} />
            <Route path="tools/multi-model-image-generator" element={<MultiModelImageGenerator />} />
            <Route path="tools/meme-creator" element={<MemeCreator />} />
            <Route path="tools/code-reviewer" element={<CodeReviewer />} />
            <Route path="tools/pdf-merger" element={<PdfMerger />} />
            <Route path="tools/youtube-downloader" element={<YoutubeToMP3 />} />
            <Route path="tools/audio-quiz" element={<AudioQuiz />} />
            <Route path="tools/audio-quiz/:id" element={<AudioQuizTake />} />
            <Route path="tools/screen-recorder" element={<ScreenRecorder />} />
            <Route path="tools/url-shortener" element={<UrlShortener />} />
            <Route path="tools/file-to-link" element={<FileToLink />} />
            <Route path="tools/clipboard-to-link" element={<ClipboardToLink />} />
            <Route path="blogs" element={<BlogList />} />
            <Route path="blogs/:id" element={<BlogDetail />} />
            <Route path="leads/audience" element={<LeadAudience />} />
            <Route path="help" element={<Help />} />
            <Route path="auth/login" element={<Login />} />
            <Route path="terms" element={<Terms />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="tools/html-playground" element={<HtmlPlayground />} />
            <Route path="tools/html-playground/preview" element={<HtmlPlaygroundPreview />} />
            <Route path="tools/qr-code-generator" element={<QRCodeGenerator />} />
            <Route path="tools/error-logging" element={<ErrorLogging />} />
            <Route path="tools/background-remover" element={<BackgroundRemover />} />
            <Route path="tools/ocr-image" element={<OcrImage />} />
            <Route path="tools/jwt-decoder" element={<JWTDecoder />} />
            <Route path="tools/uuid-generator" element={<UUIDGenerator />} />
            <Route path="tools/color-converter" element={<ColorConverter />} />
            <Route path="tools/images-to-pdf" element={<ImagesToPdf />} />
            <Route path="tools/password-generator" element={<PasswordGenerator />} />
            <Route path="tools/case-converter" element={<CaseConverter />} />
            <Route path="tools/regex-tester" element={<RegexTester />} />
            <Route path="tools/cron-translator" element={<CronTranslator />} />
            <Route path="tools/timestamp-converter" element={<TimestampConverter />} />
            <Route path="tools/base64-encoder-decoder" element={<Base64Codec />} />
            <Route path="tools/http-status-codes" element={<HttpStatusLookup />} />
            <Route path="tools/slugifier" element={<Slugifier />} />
            <Route path="tools/html-entity-encoder-decoder" element={<HtmlEntityCodec />} />
            <Route path="tools/chmod-calculator" element={<ChmodCalculator />} />
            <Route path="tools/cidr-calculator" element={<CidrCalculator />} />
            <Route path="tools/csv-json-converter" element={<CsvJsonConverter />} />
            <Route path="tools/lorem-ipsum-generator" element={<LoremIpsumGenerator />} />
            <Route path="tools/totp-generator" element={<TotpGenerator />} />
            <Route path="tools/ulid-generator" element={<UlidGenerator />} />
            <Route path="tools/hash-generator" element={<HashGenerator />} />
            <Route path="tools/number-base-converter" element={<NumberBaseConverter />} />
            <Route path="tools/json-yaml-converter" element={<JsonYamlConverter />} />
            <Route path="tools/css-unit-converter" element={<CssUnitConverter />} />
            <Route path="tools/hmac-generator" element={<HmacGenerator />} />
            <Route path="tools/markdown-table-generator" element={<MarkdownTableGenerator />} />
            <Route path="tools/json-to-typescript" element={<JsonToTypescript />} />
            <Route path="tools/mime-type-lookup" element={<MimeTypeLookup />} />
            <Route path="tools/byte-size-converter" element={<ByteSizeConverter />} />
            <Route path="tools/color-contrast-checker" element={<ColorContrastChecker />} />
            <Route path="tools/xml-formatter" element={<XmlFormatter />} />
            <Route path="tools/date-difference-calculator" element={<DateDifferenceCalculator />} />
            <Route path="tools/markdown-toc-generator" element={<MarkdownTocGenerator />} />
            <Route path="tools/unicode-inspector" element={<UnicodeInspector />} />
            <Route path="tools/box-shadow-generator" element={<BoxShadowGenerator />} />
            <Route path="tools/css-gradient-generator" element={<CssGradientGenerator />} />
            <Route path="tools/css-clamp-generator" element={<CssClampGenerator />} />
            <Route path="tools/json-path-tester" element={<JsonPathTester />} />
            <Route path="tools/line-sorter" element={<LineSorter />} />
            <Route path="tools/mongo-objectid-inspector" element={<MongoObjectIdInspector />} />
            <Route path="tools/semver-comparator" element={<SemverComparator />} />
            <Route path="tools/css-specificity-calculator" element={<CssSpecificityCalculator />} />
            <Route path="tools/keyword-research" element={<KeywordResearch />} />
            <Route path="tools/roman-numeral-converter" element={<RomanNumeralConverter />} />
            <Route path="tools/url-parser" element={<UrlParser />} />
            <Route path="tools/base32-encoder-decoder" element={<Base32Codec />} />
            <Route path="tools/punycode-converter" element={<PunycodeConverter />} />
            <Route path="tools/luhn-validator" element={<LuhnValidator />} />
            <Route path="tools/request-inspector" element={<RequestInspector />} />
          </Route>

          {/* Updated fallback routes with query preservation */}
          <Route path="/tools/url-encoder" element={<FallbackWithQuery to="/en/tools/url-encoder" />} />
          <Route path="/tools/url-decoder" element={<FallbackWithQuery to="/en/tools/url-decoder" />} />
          <Route path="/tools/url-scraper" element={<FallbackWithQuery to="/en/tools/url-scraper" />} />
          <Route path="/tools/remove-line-breaks" element={<FallbackWithQuery to="/en/tools/remove-line-breaks" />} />
          <Route path="/tools/render-string" element={<FallbackWithQuery to="/en/tools/render-string" />} />
          <Route path="/tools/render-markdown" element={<FallbackWithQuery to="/en/tools/render-markdown" />} />
          <Route path="/tools/query-param-editor" element={<FallbackWithQuery to="/en/tools/query-param-editor" />} />
          <Route path="/tools/json-formatter" element={<FallbackWithQuery to="/en/tools/json-formatter" />} />
          <Route path="/tools/api-tester" element={<FallbackWithQuery to="/en/tools/api-tester" />} />
          <Route path="/tools/smtp-tester" element={<FallbackWithQuery to="/en/tools/smtp-tester" />} />
          <Route path="/tools/timezone-converter" element={<FallbackWithQuery to="/en/tools/timezone-converter" />} />
          <Route path="/tools/image-resizer" element={<FallbackWithQuery to="/en/tools/image-resizer" />} />
          <Route path="/tools/pdf-to-text-converter" element={<FallbackWithQuery to="/en/tools/pdf-to-text-converter" />} />
          <Route path="/tools/pdf-to-csv-converter" element={<FallbackWithQuery to="/en/tools/pdf-to-csv-converter" />} />
          <Route path="/tools/pdf-to-html-converter" element={<FallbackWithQuery to="/en/tools/pdf-to-html-converter" />} />
          <Route path="/tools/pdf-splitter" element={<FallbackWithQuery to="/en/tools/pdf-splitter" />} />
          <Route path="/tools/pdf-to-image-converter" element={<FallbackWithQuery to="/en/tools/pdf-to-image-converter" />} />
          <Route path="/tools/pdf-compressor" element={<FallbackWithQuery to="/en/tools/pdf-compressor" />} />
          <Route path="/tools/text-to-speech" element={<FallbackWithQuery to="/en/tools/text-to-speech" />} />
          <Route path="/tools/text-compare" element={<FallbackWithQuery to="/en/tools/text-compare" />} />
          <Route path="/tools/text-counter" element={<FallbackWithQuery to="/en/tools/text-counter" />} />
          <Route path="/tools/js-playground" element={<FallbackWithQuery to="/en/tools/js-playground" />} />
          <Route path="/tools/email-verifier" element={<FallbackWithQuery to="/en/tools/email-verifier" />} />
          <Route path="/tools/token-counter" element={<FallbackWithQuery to="/en/tools/token-counter" />} />
          <Route path="/tools/multi-model-playground" element={<FallbackWithQuery to="/en/tools/multi-model-playground" />} />
          <Route path="/tools/multi-model-image-generator" element={<FallbackWithQuery to="/en/tools/multi-model-image-generator" />} />
          <Route path="/tools/multi-model-transcription" element={<FallbackWithQuery to="/en/tools/multi-model-transcription" />} />
          <Route path="/tools/meme-creator" element={<FallbackWithQuery to="/en/tools/meme-creator" />} />
          <Route path="/tools/code-reviewer" element={<FallbackWithQuery to="/en/tools/code-reviewer" />} />
          <Route path="/tools/pdf-merger" element={<FallbackWithQuery to="/en/tools/pdf-merger" />} />
          <Route path="/tools/youtube-downloader" element={<FallbackWithQuery to="/en/tools/youtube-downloader" />} />
          <Route path="/tools/audio-quiz" element={<FallbackWithQuery to="/en/tools/audio-quiz" />} />
          <Route path="/tools/audio-quiz/:id" element={<FallbackWithQuery to="/en/tools/audio-quiz/:id" />} />
          <Route path="/tools/screen-recorder" element={<FallbackWithQuery to="/en/tools/screen-recorder" />} />
          <Route path="/tools/url-shortener" element={<FallbackWithQuery to="/en/tools/url-shortener" />} />
          <Route path="/tools/file-to-link" element={<FallbackWithQuery to="/en/tools/file-to-link" />} />
          <Route path="/tools/clipboard-to-link" element={<FallbackWithQuery to="/en/tools/clipboard-to-link" />} />
          <Route path="/tools/html-playground" element={<FallbackWithQuery to="/en/tools/html-playground" />} />
          <Route path="/tools/html-playground/preview" element={<FallbackWithQuery to="/en/tools/html-playground/preview" />} />
          <Route path="/tools/qr-code-generator" element={<FallbackWithQuery to="/en/tools/qr-code-generator" />} />
          <Route path="/tools/background-remover" element={<FallbackWithQuery to="/en/tools/background-remover" />} />
          <Route path="/tools/ocr-image" element={<FallbackWithQuery to="/en/tools/ocr-image" />} />
          <Route path="/tools/jwt-decoder" element={<FallbackWithQuery to="/en/tools/jwt-decoder" />} />
          <Route path="/tools/uuid-generator" element={<FallbackWithQuery to="/en/tools/uuid-generator" />} />
          <Route path="/tools/color-converter" element={<FallbackWithQuery to="/en/tools/color-converter" />} />
          <Route path="/tools/images-to-pdf" element={<FallbackWithQuery to="/en/tools/images-to-pdf" />} />
          <Route path="/tools/password-generator" element={<FallbackWithQuery to="/en/tools/password-generator" />} />
          <Route path="/tools/case-converter" element={<FallbackWithQuery to="/en/tools/case-converter" />} />
          <Route path="/tools/regex-tester" element={<FallbackWithQuery to="/en/tools/regex-tester" />} />
          <Route path="/tools/cron-translator" element={<FallbackWithQuery to="/en/tools/cron-translator" />} />
          <Route path="/tools/timestamp-converter" element={<FallbackWithQuery to="/en/tools/timestamp-converter" />} />
          <Route path="/tools/base64-encoder-decoder" element={<FallbackWithQuery to="/en/tools/base64-encoder-decoder" />} />
          <Route path="/tools/http-status-codes" element={<FallbackWithQuery to="/en/tools/http-status-codes" />} />
          <Route path="/tools/slugifier" element={<FallbackWithQuery to="/en/tools/slugifier" />} />
          <Route path="/tools/html-entity-encoder-decoder" element={<FallbackWithQuery to="/en/tools/html-entity-encoder-decoder" />} />
          <Route path="/tools/chmod-calculator" element={<FallbackWithQuery to="/en/tools/chmod-calculator" />} />
          <Route path="/tools/cidr-calculator" element={<FallbackWithQuery to="/en/tools/cidr-calculator" />} />
          <Route path="/tools/csv-json-converter" element={<FallbackWithQuery to="/en/tools/csv-json-converter" />} />
          <Route path="/tools/lorem-ipsum-generator" element={<FallbackWithQuery to="/en/tools/lorem-ipsum-generator" />} />
          <Route path="/tools/totp-generator" element={<FallbackWithQuery to="/en/tools/totp-generator" />} />
          <Route path="/tools/ulid-generator" element={<FallbackWithQuery to="/en/tools/ulid-generator" />} />
          <Route path="/tools/hash-generator" element={<FallbackWithQuery to="/en/tools/hash-generator" />} />
          <Route path="/tools/number-base-converter" element={<FallbackWithQuery to="/en/tools/number-base-converter" />} />
          <Route path="/tools/json-yaml-converter" element={<FallbackWithQuery to="/en/tools/json-yaml-converter" />} />
          <Route path="/tools/css-unit-converter" element={<FallbackWithQuery to="/en/tools/css-unit-converter" />} />
          <Route path="/tools/hmac-generator" element={<FallbackWithQuery to="/en/tools/hmac-generator" />} />
          <Route path="/tools/markdown-table-generator" element={<FallbackWithQuery to="/en/tools/markdown-table-generator" />} />
          <Route path="/tools/json-to-typescript" element={<FallbackWithQuery to="/en/tools/json-to-typescript" />} />
          <Route path="/tools/mime-type-lookup" element={<FallbackWithQuery to="/en/tools/mime-type-lookup" />} />
          <Route path="/tools/byte-size-converter" element={<FallbackWithQuery to="/en/tools/byte-size-converter" />} />
          <Route path="/tools/color-contrast-checker" element={<FallbackWithQuery to="/en/tools/color-contrast-checker" />} />
          <Route path="/tools/xml-formatter" element={<FallbackWithQuery to="/en/tools/xml-formatter" />} />
          <Route path="/tools/date-difference-calculator" element={<FallbackWithQuery to="/en/tools/date-difference-calculator" />} />
          <Route path="/tools/markdown-toc-generator" element={<FallbackWithQuery to="/en/tools/markdown-toc-generator" />} />
          <Route path="/tools/unicode-inspector" element={<FallbackWithQuery to="/en/tools/unicode-inspector" />} />
          <Route path="/tools/box-shadow-generator" element={<FallbackWithQuery to="/en/tools/box-shadow-generator" />} />
          <Route path="/tools/css-gradient-generator" element={<FallbackWithQuery to="/en/tools/css-gradient-generator" />} />
          <Route path="/tools/css-clamp-generator" element={<FallbackWithQuery to="/en/tools/css-clamp-generator" />} />
          <Route path="/tools/json-path-tester" element={<FallbackWithQuery to="/en/tools/json-path-tester" />} />
          <Route path="/tools/line-sorter" element={<FallbackWithQuery to="/en/tools/line-sorter" />} />
          <Route path="/tools/mongo-objectid-inspector" element={<FallbackWithQuery to="/en/tools/mongo-objectid-inspector" />} />
          <Route path="/tools/semver-comparator" element={<FallbackWithQuery to="/en/tools/semver-comparator" />} />
          <Route path="/tools/css-specificity-calculator" element={<FallbackWithQuery to="/en/tools/css-specificity-calculator" />} />
          <Route path="/tools/keyword-research" element={<FallbackWithQuery to="/en/tools/keyword-research" />} />
          <Route path="/tools/roman-numeral-converter" element={<FallbackWithQuery to="/en/tools/roman-numeral-converter" />} />
          <Route path="/tools/url-parser" element={<FallbackWithQuery to="/en/tools/url-parser" />} />
          <Route path="/tools/base32-encoder-decoder" element={<FallbackWithQuery to="/en/tools/base32-encoder-decoder" />} />
          <Route path="/tools/punycode-converter" element={<FallbackWithQuery to="/en/tools/punycode-converter" />} />
          <Route path="/tools/luhn-validator" element={<FallbackWithQuery to="/en/tools/luhn-validator" />} />
          <Route path="/tools/request-inspector" element={<FallbackWithQuery to="/en/tools/request-inspector" />} />

          {/* Blog fallback routes */}
          <Route path="/blogs" element={<FallbackWithQuery to="/en/blogs" />} />
          <Route path="/blogs/:id" element={<FallbackWithQuery to="/en/blogs/:id" />} />
          
          {/* Lead management fallback routes */}
          <Route path="/leads/audience" element={<FallbackWithQuery to="/en/leads/audience" />} />
          
          {/* Authentication fallback routes */}
          <Route path="/auth/login" element={<FallbackWithQuery to="/en/auth/login" />} />
          
          {/* Static pages fallback routes */}
          <Route path="/help" element={<FallbackWithQuery to="/en/help" />} />
          <Route path="/terms" element={<FallbackWithQuery to="/en/terms" />} />
          <Route path="/privacy" element={<FallbackWithQuery to="/en/privacy" />} />
          <Route path="/tools/background-remover" element={<FallbackWithQuery to="/en/tools/background-remover" />} />
          
          {/* Catch-all for any other paths without language prefix */}
          <Route path="*" element={<Navigate to="/en/" replace />} />
        </Routes>
      </LanguageEnforcer>
    </Router>
  );
}

export default App;